import React, { useState, useCallback, useEffect, useRef } from "react";
import ChatPanel from "./components/ChatPanel";
import DocumentsPanel from "./components/DocumentsPanel";
import ConversationSidebar from "./components/ConversationSidebar";
import IncidentsPanel from "./components/IncidentsPanel";
import { msalEnabled } from "./auth/msalConfig";
import { api } from "./api";
import "./App.css";

// Lazy import so the module is only evaluated when MSAL is actually enabled.
// This avoids "useMsal must be used inside MsalProvider" crashes in dev mode.
const MsalAuthSetup = msalEnabled
  ? React.lazy(() => import("./auth/MsalAuthSetup"))
  : null;

type Tab = "chat" | "documents" | "incidents";

interface UserInfo { name?: string | null; email?: string | null; }

function initials(user: UserInfo | null): string {
  if (!user) return "?";
  const name = user.name ?? user.email ?? "";
  // Take first letter of each word (max 2)
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join("") || "?";
}

function App() {
  const [activeTab, setActiveTab]           = useState<Tab>("chat");
  const [selectedConvId, setSelectedConvId] = useState<string | undefined>();
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);
  const [user, setUser]                     = useState<UserInfo | null>(null);
  const [doLogout, setDoLogout]             = useState<(() => void) | null>(null);
  const [avatarOpen, setAvatarOpen]         = useState(false);
  const avatarRef                           = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!avatarOpen) return;
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [avatarOpen]);

  useEffect(() => {
    if (msalEnabled) return;
    api.getMe()
      .then(u => setUser({ name: u.name, email: u.email }))
      .catch(() => setUser(null));
  }, []);

  const handleConversationChange = useCallback((id: string) => {
    setSelectedConvId(id);
    setSidebarRefreshKey(k => k + 1);
  }, []);

  const handleNewChat = useCallback(() => setSelectedConvId(undefined), []);

  const handleLogout = useCallback((fn: () => void) => {
    setDoLogout(() => fn);
  }, []);

  const userInitials = initials(user);
  const userLabel    = user?.name ?? user?.email ?? (msalEnabled ? "Signing in…" : "Dev User");

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-content">
          <div className="header-logo">🔍</div>
          <div>
            <h1>Izon Document AI</h1>
            <p>Answers sourced from internal company documents</p>
          </div>
        </div>

        <nav className="tab-nav">
          <button
            className={activeTab === "chat" ? "tab active" : "tab"}
            onClick={() => setActiveTab("chat")}
          >
            Ask a Question
          </button>
          <button
            className={activeTab === "documents" ? "tab active" : "tab"}
            onClick={() => setActiveTab("documents")}
          >
            Manage Documents
          </button>
          <button
            className={activeTab === "incidents" ? "tab active" : "tab"}
            onClick={() => setActiveTab("incidents")}
          >
            Incidents
          </button>
        </nav>

        {/* ── User avatar + sign-out dropdown ───────────────────────────── */}
        <div className="user-avatar" ref={avatarRef}>
          <button
            className="avatar-trigger"
            onClick={() => msalEnabled && setAvatarOpen(o => !o)}
            title={msalEnabled ? "Account options" : userLabel}
          >
            <div className="avatar-circle">{userInitials}</div>
            <span className="avatar-label">{userLabel}</span>
            {msalEnabled && (
              <svg className="avatar-chevron" width="10" height="10" viewBox="0 0 10 6" fill="none">
                <path d={avatarOpen ? "M9 5L5 1L1 5" : "M1 1L5 5L9 1"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            )}
          </button>

          {avatarOpen && (
            <div className="avatar-dropdown">
              <div className="avatar-dropdown-info">
                {user?.name  && <div className="avatar-dropdown-name">{user.name}</div>}
                {user?.email && <div className="avatar-dropdown-email">{user.email}</div>}
              </div>
              <div className="avatar-dropdown-divider" />
              <button
                className="avatar-dropdown-signout"
                onClick={() => { setAvatarOpen(false); doLogout?.(); }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {msalEnabled && MsalAuthSetup && (
        <React.Suspense fallback={null}>
          <MsalAuthSetup
            onUser={setUser}
            onLogout={handleLogout}
          />
        </React.Suspense>
      )}

      <main className="app-main">
        {activeTab === "chat" && (
          <div className="chat-layout">
            <ConversationSidebar
              selectedId={selectedConvId}
              refreshKey={sidebarRefreshKey}
              onSelect={setSelectedConvId}
              onNew={handleNewChat}
            />
            <ChatPanel
              conversationId={selectedConvId}
              onConversationChange={handleConversationChange}
            />
          </div>
        )}
        {activeTab === "documents" && <DocumentsPanel />}
        {activeTab === "incidents" && <IncidentsPanel />}
      </main>
    </div>
  );
}

export default App;
